import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import NewQuotationView from '../components/plantProposal/NewQuotationView';
import ClientSelectionModal from '../components/proposal/ClientSelectionModal';

export default function PlantProposal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [createMode, setCreateMode] = useState(false);
  const [returnQuotationData, setReturnQuotationData] = useState(null);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientModalPrefill, setClientModalPrefill] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (location.state?.returnToEdit && location.state?.quotationData) {
      setReturnQuotationData(location.state.quotationData);
      setSelectedClient(location.state.quotationData.selectedClient ?? null);
      setCreateMode(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const handleViewPrevious = () => {
    navigate('/generatequotation');
  };

  const handleCreateNew = () => {
    setReturnQuotationData(null);
    setSelectedClient(null);
    setClientModalPrefill(null);
    setClientModalOpen(true);
  };

  const handleClientModalClose = () => {
    setClientModalOpen(false);
    setClientModalPrefill(null);
  };

  const handleClientConfirmed = (client) => {
    setSelectedClient(client);
    setCreateMode(true);
    setClientModalPrefill(null);
  };

  const handleEditClient = () => {
    if (!selectedClient) return;
    setClientModalPrefill(selectedClient);
    setClientModalOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateMode(false);
    setReturnQuotationData(null);
    setSelectedClient(null);
  };

  const handleGenerateQuotation = (quotationState) => {
    setCreateMode(false);
    navigate('/generatequotation', { state: { quotationData: quotationState } });
  };

  if (createMode) {
    return (
      <Box sx={{ height: 'calc(100vh - 56px)', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <NewQuotationView
          onClose={handleCloseCreate}
          onGenerateQuotation={handleGenerateQuotation}
          initialQuotationData={returnQuotationData}
          selectedClient={selectedClient}
          onEditClient={handleEditClient}
        />
        <ClientSelectionModal
          open={clientModalOpen}
          onClose={handleClientModalClose}
          onClientConfirmed={handleClientConfirmed}
          prefillClient={clientModalPrefill}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)',
        px: 2,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
        Plant Proposal
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 640 }}>
        <Card
          elevation={0}
          sx={{
            width: 280,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: 4,
              transform: 'translateY(-2px)',
            },
          }}
        >
          <CardActionArea onClick={handleViewPrevious} sx={{ p: 2, minHeight: 160 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <HistoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                View Previous Quotations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Open and download existing quotations
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card
          elevation={0}
          sx={{
            width: 280,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: 4,
              transform: 'translateY(-2px)',
            },
          }}
        >
          <CardActionArea onClick={handleCreateNew} sx={{ p: 2, minHeight: 160 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Create New
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Build a new quotation with products and specs
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
      <ClientSelectionModal
        open={clientModalOpen}
        onClose={handleClientModalClose}
        onClientConfirmed={handleClientConfirmed}
        prefillClient={clientModalPrefill}
      />
    </Box>
  );
}
